/**
 * Booking payment helpers shared by the Booking Details page and the schedule
 * calendar's member dialog, so both show the same base price, discount,
 * payment status and dates for a booking.
 */

/** The booking fields these helpers read (the populated getBookingById shape). */
export interface PaymentBookingLike {
  isPaid?: boolean;
  quantity?: number;
  session?: { price?: number } | null;
  event?: { price?: number } | null;
  wellnessService?: { price?: number } | null;
  transaction?: {
    basePrice?: number;
    taxes?: number;
    modeOfPayment?: string;
    coupon?: {
      code?: string;
      discountType?: 'percentage' | 'amount';
      discountValue?: number;
    } | null;
    promoCode?: {
      code?: string;
      value?: number;
      type?: 'percentage' | 'amount';
    } | null;
  } | null;
}

// applyOffset=true for event dates (stored as IST-labeled-UTC, need offset correction)
// applyOffset=false for workout/session dates (stored as proper UTC, just convert to IST)
export const formatDateIST = (
  dateString: string | null | undefined,
  format: 'date' | 'time' | 'datetime' = 'datetime',
  applyOffset = true,
): string => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';

    const istOffsetMs = 5.5 * 60 * 60 * 1000;
    const adjustedDate = applyOffset ? new Date(date.getTime() - istOffsetMs) : date;

    if (format === 'date') {
      return adjustedDate.toLocaleDateString('en-IN', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        timeZone: 'Asia/Kolkata',
      });
    } else if (format === 'time') {
      return adjustedDate.toLocaleTimeString('en-IN', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
        timeZone: 'Asia/Kolkata',
      });
    } else {
      return adjustedDate.toLocaleString('en-IN', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
        timeZone: 'Asia/Kolkata',
      });
    }
  } catch {
    return 'N/A';
  }
};

const round2 = (n: number) => Math.round(n * 100) / 100;

export type DiscountBreakdown = {
  code?: string;
  discountAmount: number;
  originalBase: number;
  isEstimated: boolean;
  source: 'coupon' | 'promo';
  rateType: 'percentage' | 'amount';
  rateValue: number;
};

export const getSourceLabel = (source: 'coupon' | 'promo') =>
  source === 'coupon' ? 'Coupon' : 'Promo Code';

export const formatDiscountRate = (b: DiscountBreakdown): string =>
  b.rateType === 'percentage' ? `${b.rateValue}% off` : `₹${b.rateValue} off`;

export const getDiscountBreakdown = (b: PaymentBookingLike): DiscountBreakdown | null => {
  const transaction = b.transaction;
  const coupon = transaction?.coupon;
  const promo = coupon ? null : transaction?.promoCode;
  if (!coupon && !promo) return null;

  const paidBase = transaction?.basePrice ?? 0;
  const listPrice = round2(
    (b.session?.price ?? b.event?.price ?? b.wellnessService?.price ?? 0) *
      (b.quantity ?? 1),
  );

  const source: 'coupon' | 'promo' = coupon ? 'coupon' : 'promo';
  const code = coupon ? coupon.code : promo!.code;
  const rateType: 'percentage' | 'amount' = coupon
    ? (coupon.discountType === 'amount' ? 'amount' : 'percentage')
    : (promo!.type === 'amount' ? 'amount' : 'percentage');
  const rateValue = coupon ? (coupon.discountValue ?? 0) : (promo!.value ?? 0);

  if (listPrice > 0) {
    const originalBase = listPrice;
    const discountAmount = Math.max(0, round2(originalBase - paidBase));
    return { code, discountAmount, originalBase, isEstimated: false, source, rateType, rateValue };
  }

  if (rateType === 'amount') {
    const discountAmount = round2(rateValue);
    const originalBase = round2(paidBase + discountAmount);
    return { code, discountAmount, originalBase, isEstimated: true, source, rateType, rateValue };
  }
  if (rateValue >= 100) {
    return { code, discountAmount: paidBase, originalBase: paidBase, isEstimated: true, source, rateType, rateValue };
  }
  const scale = 1 / (1 - rateValue / 100);
  const originalBase = round2(paidBase * scale);
  const discountAmount = round2(originalBase - paidBase);
  return { code, discountAmount, originalBase, isEstimated: true, source, rateType, rateValue };
};

export const getPaymentStatus = (b: PaymentBookingLike): string => {
  const mode = b.transaction?.modeOfPayment;
  const breakdown = getDiscountBreakdown(b);
  const amount = (b.transaction?.basePrice ?? 0) + (b.transaction?.taxes ?? 0);

  if (mode === 'subscription') return 'Pack';
  if (breakdown && amount === 0) return 'Free (100% Off)';
  if (mode === 'free') return 'Free';
  if (mode === 'cash') return b.isPaid === false ? 'Pending' : 'Cash';
  if (mode === 'online') return b.isPaid === false ? 'Pending' : 'Online';
  if (breakdown && amount > 0 && b.isPaid !== false) {
    return `Paid (${getSourceLabel(breakdown.source)} Applied)`;
  }
  if (b.isPaid === false) return 'Pending';
  return 'Paid';
};

export const getPaymentStatusStyles = (status: string) => {
  switch (status) {
    case 'Paid':
    case 'Online':
    case 'Cash':
      return { bg: '#e8f5e9', color: '#1b5e20', border: '#a5d6a7' };
    case 'Pack':
      return { bg: '#f3e5f5', color: '#6a1b9a', border: '#ce93d8' };
    case 'Free':
      return { bg: '#e8f5e9', color: '#2e7d32', border: '#81c784' };
    case 'Coupon Applied':
    case 'Paid (Coupon Applied)':
    case 'Paid (Promo Code Applied)':
      return { bg: '#e0f2f1', color: '#00695c', border: '#80cbc4' };
    case 'Free (100% Off)':
      return { bg: '#e8f5e9', color: '#2e7d32', border: '#81c784' };
    case 'Pending':
      return { bg: '#fff4e5', color: '#8a6d3b', border: '#ffe1b3' };
    default:
      return { bg: '#f5f5f5', color: '#616161', border: '#e0e0e0' };
  }
};

export const getBookingAmount = (b: PaymentBookingLike) =>
  (b.transaction?.basePrice || 0) + (b.transaction?.taxes || 0);
